import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import {
  SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import { Agent, CreateAgentDto, UpdateAgentDto } from '../../../types/agent';
import { Lab } from '../../../types/lab';
import { agentsService } from '../../../services/agentsService';
import { labsService } from '../../../services/labsService';
import { DialogHeader, AddressSection, SageDetailsSection, buildSageDetails, EMPTY_SAGE_FIELDS, mapSageDetailsToFormFields, mapServerErrorsToFormErrors, createFieldChangeHandler, AdditionalEmailsEditor, validateAdditionalEmails, cleanAdditionalEmails } from '../../Common';
import type { SageFormFields } from '../../Common';
import { useSnackbar } from 'notistack';
import { validateEmail } from '../../../utils/validation';
import { sageTaxTypeService } from '../../../services/sageTaxTypeService';
import type { SageTaxType } from '../../../types/sageTaxType';
import { userManagementService } from '../../../services/userManagementService';
import { AgentSalesRepLinkPanel } from './AgentSalesRepLinkPanel';

/** Minimal linked-user option for the Autocomplete (id + display bits). */
type LinkedUserOption = { idUser: number; label: string };

interface AgentDialogProps {
  open: boolean;
  agent: Agent | null;
  onClose: (saved: boolean) => void;
  onCreated?: (agent: Agent) => void;
  initialTab?: number;
}

type AgentFormData = {
  idLab: number;
  agentName: string;
  contactNumber: string;
  emailAddress: string;
  additionalEmails: string[];
  invoicingMode: string;
  defaultSageTaxTypeId: number | null;
} & SageFormFields;

const EMPTY_FORM: AgentFormData = {
  idLab: 0,
  agentName: '',
  contactNumber: '',
  emailAddress: '',
  additionalEmails: [],
  invoicingMode: 'Standard',
  defaultSageTaxTypeId: null,
  ...EMPTY_SAGE_FIELDS,
};

const AgentDialog: React.FC<AgentDialogProps> = ({ open, agent, onClose, onCreated, initialTab }) => {
  const [formData, setFormData] = useState<AgentFormData>(EMPTY_FORM);
  const [activeTab, setActiveTab] = useState(0);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingLabs, setLoadingLabs] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();
  const [taxTypes, setTaxTypes] = useState<SageTaxType[]>([]);
  // Sage tab actions (create/link/sync/unlink) apply live against the backend. Track whether
  // any of them changed state so that closing the dialog refreshes the parent list.
  const [sageChanged, setSageChanged] = useState(false);
  const [linkedUser, setLinkedUser] = useState<LinkedUserOption | null>(null);
  const [userOptions, setUserOptions] = useState<LinkedUserOption[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  const fetchLabs = useCallback(async () => {
    setLoadingLabs(true);
    try {
      const response = await labsService.getAll();
      if (response.success && response.data) {
        setLabs(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch labs');
      }
    } catch (error: unknown) {
      const err = error as { message?: string };
      enqueueSnackbar(err.message || 'Failed to load labs', { variant: 'error' });
      setLabs([]);
    } finally {
      setLoadingLabs(false);
    }
  }, [enqueueSnackbar]);

  // Fetch labs when dialog opens
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch-on-open; loading flag is set synchronously by fetchLabs
      fetchLabs();
    }
  }, [open, fetchLabs]);

  useEffect(() => {
    if (open && agent) {
      sageTaxTypeService.getAllLocal().then(res => setTaxTypes(res.data ?? [])).catch(() => {});
    }
  }, [open, agent]);

  useEffect(() => {
    if (agent) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset dialog form when agent/open changes; state-during-render refactor not clearly safe here
      setFormData({
        idLab: agent.idLab,
        agentName: agent.agentName,
        contactNumber: agent.contactNumber || '',
        emailAddress: agent.emailAddress || '',
        additionalEmails: agent.additionalEmails ? [...agent.additionalEmails] : [],
        invoicingMode: agent.invoicingMode || 'Standard',
        defaultSageTaxTypeId: agent.defaultSageTaxTypeId ?? null,
        ...mapSageDetailsToFormFields(agent.sageDetails),
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
    setActiveTab(initialTab ?? 0);
    setSageChanged(false);
    setLinkedUser(
      agent?.userId != null
        ? {
            idUser: agent.userId,
            label: agent.linkedUserName
              ? `${agent.linkedUserName}${agent.linkedUserEmail ? ` (${agent.linkedUserEmail})` : ''}`
              : `User #${agent.userId}`,
          }
        : null,
    );
    setUserSearch('');
  }, [agent, open, initialTab]);

  // Debounced server-side user search for the linked-user picker (300ms house rule).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingUsers(true);
      try {
        const res = await userManagementService.getUsers({
          searchTerm: userSearch || undefined,
          isActive: true,
          pageSize: 100,
        });
        if (!cancelled) {
          setUserOptions(
            (res.users ?? []).map((u) => ({
              idUser: u.idUser,
              label: `${u.firstName} ${u.lastName}`.trim()
                ? `${`${u.firstName} ${u.lastName}`.trim()} (${u.email})`
                : u.email,
            })),
          );
        }
      } catch {
        if (!cancelled) setUserOptions([]);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, userSearch]);

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.agentName.trim()) {
      newErrors.agentName = 'Agent name is required';
    } else if (formData.agentName.trim().length < 2) {
      newErrors.agentName = 'Agent name must be at least 2 characters';
    } else if (formData.agentName.trim().length > 100) {
      newErrors.agentName = 'Agent name must be less than 100 characters';
    }

    if (!formData.idLab || formData.idLab === 0) {
      newErrors.idLab = 'Lab is required';
    }

    if (formData.emailAddress && !validateEmail(formData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }

    // Additional emails: each non-blank must be valid, unique, and not equal the primary.
    const additionalEmailsError = validateAdditionalEmails(formData.additionalEmails, formData.emailAddress);
    if (additionalEmailsError) {
      newErrors.additionalEmails = additionalEmailsError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      setActiveTab(0);
      return;
    }

    setLoading(true);
    try {
      const sageDetails = buildSageDetails(formData);
      const cleanedAdditionalEmails = cleanAdditionalEmails(formData.additionalEmails);

      if (agent) {
        const updateDto: UpdateAgentDto = {
          idLab: formData.idLab,
          agentName: formData.agentName,
          contactNumber: formData.contactNumber || undefined,
          emailAddress: formData.emailAddress || undefined,
          additionalEmails: cleanedAdditionalEmails,
          invoicingMode: formData.invoicingMode,
          defaultSageTaxTypeId: formData.defaultSageTaxTypeId,
          userId: linkedUser?.idUser ?? null,
          sageDetails,
        };
        await agentsService.update(agent.idAgent, updateDto);
        enqueueSnackbar('Agent updated successfully', { variant: 'success' });
      } else {
        const createDto: CreateAgentDto = {
          idLab: formData.idLab,
          agentName: formData.agentName,
          contactNumber: formData.contactNumber || undefined,
          emailAddress: formData.emailAddress || undefined,
          additionalEmails: cleanedAdditionalEmails,
          userId: linkedUser?.idUser ?? null,
          sageDetails,
        };
        const created = await agentsService.create(createDto);
        enqueueSnackbar('Agent created successfully', { variant: 'success' });
        onClose(true);
        onCreated?.(created);
        return;
      }

      onClose(true);
    } catch (error: unknown) {
      mapServerErrorsToFormErrors(error, setErrors, enqueueSnackbar, 'agent');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // If a Sage action changed state while the dialog was open, refresh the parent list on close.
      onClose(sageChanged);
    }
  };

  const handleFieldChange = createFieldChangeHandler<AgentFormData>(setFormData, setErrors);

  const clearAdditionalEmailsError = () =>
    setErrors((prev) => {
      if (!prev.additionalEmails) return prev;
      const { additionalEmails: _omit, ...rest } = prev;
      return rest;
    });

  const handleAdditionalEmailsChange = (next: string[]) => {
    setFormData((prev) => ({ ...prev, additionalEmails: next }));
  };

  const suggestedSageName = formData.agentName.trim();
  const isSageReady = !!formData.sageName?.trim();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 2 },
        },
      }}
    >
      <DialogHeader
        title={agent ? 'Edit Agent' : 'Add New Agent'}
        icon={<SupportAgentIcon sx={{ color: 'primary.main' }} />}
        onClose={handleClose}
        disabled={loading}
      />

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        aria-label="Agent form tabs"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 2,
          '& .MuiTab-root': { textTransform: 'none' },
        }}
      >
        <Tab label="Basic Info" id="agent-tab-basic" aria-controls="agent-tabpanel-basic" />
        <Tab label="Addresses" id="agent-tab-address" aria-controls="agent-tabpanel-address" />
        <Tab label="Sage" id="agent-tab-sage" aria-controls="agent-tabpanel-sage" />
      </Tabs>

      <DialogContent sx={{ pt: 3, minHeight: 300 }}>
        {/* Tab 0: Basic Info */}
        {activeTab === 0 && (
          <Box role="tabpanel" id="agent-tabpanel-basic" aria-labelledby="agent-tab-basic" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Agent Name"
              fullWidth
              value={formData.agentName}
              onChange={(e) => handleFieldChange('agentName', e.target.value)}
              error={!!errors.agentName}
              helperText={errors.agentName || 'Enter the full name of the agent'}
              disabled={loading}
              autoFocus
              required
              slotProps={{ input: { sx: { borderRadius: 2 } } }}
            />

            <FormControl
              fullWidth
              error={!!errors.idLab}
              disabled={loading || loadingLabs}
              required
            >
              <InputLabel>Lab</InputLabel>
              <Select
                value={labs.length > 0 ? formData.idLab : ''}
                label="Lab"
                onChange={(e) => handleFieldChange('idLab', Number(e.target.value))}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value={0} disabled>
                  <em>Select a lab</em>
                </MenuItem>
                {labs.map((lab) => (
                  <MenuItem key={lab.idLab} value={lab.idLab}>
                    {lab.labName}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {errors.idLab || 'Select the lab this agent is associated with'}
              </FormHelperText>
            </FormControl>

            <TextField
              label="Contact Number"
              fullWidth
              value={formData.contactNumber}
              onChange={(e) => handleFieldChange('contactNumber', e.target.value)}
              error={!!errors.contactNumber}
              helperText={errors.contactNumber || 'Enter the agent\'s phone number (optional)'}
              disabled={loading}
              slotProps={{ input: { sx: { borderRadius: 2 } } }}
            />

            <TextField
              label="Email Address"
              fullWidth
              type="email"
              value={formData.emailAddress}
              onChange={(e) => handleFieldChange('emailAddress', e.target.value)}
              error={!!errors.emailAddress}
              helperText={errors.emailAddress || 'Enter the agent\'s email address (optional)'}
              disabled={loading}
              slotProps={{ input: { sx: { borderRadius: 2 } } }}
            />

            <AdditionalEmailsEditor
              value={formData.additionalEmails}
              onChange={handleAdditionalEmailsChange}
              error={errors.additionalEmails}
              onClearError={clearAdditionalEmailsError}
              disabled={loading}
              description="Optional extra recipients (e.g. accounts inbox) offered when sending invoices and credit notes."
            />

            <Autocomplete
              options={userOptions}
              value={linkedUser}
              onChange={(_, value) => setLinkedUser(value)}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') setUserSearch(value);
              }}
              filterOptions={(x) => x}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.idUser === v.idUser}
              loading={loadingUsers}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Linked user"
                  helperText="An active linked user is required to enable booking for this agent."
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      sx: { borderRadius: 2 },
                      endAdornment: (
                        <>
                          {loadingUsers ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />

            {agent && (
              <Box sx={(theme) => ({
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                ...theme.applyStyles('dark', { backgroundColor: 'grey.800' }),
              })}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Agent ID: #{agent.idAgent}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Tab 1: Addresses */}
        {activeTab === 1 && (
          <Box role="tabpanel" id="agent-tabpanel-address" aria-labelledby="agent-tab-address">
            <AddressSection
              postalAddress01={formData.postalAddress01}
              postalAddress02={formData.postalAddress02}
              postalAddress03={formData.postalAddress03}
              postalAddress04={formData.postalAddress04}
              postalAddress05={formData.postalAddress05}
              deliveryAddress01={formData.deliveryAddress01}
              deliveryAddress02={formData.deliveryAddress02}
              deliveryAddress03={formData.deliveryAddress03}
              deliveryAddress04={formData.deliveryAddress04}
              deliveryAddress05={formData.deliveryAddress05}
              onChange={(field, value) => handleFieldChange(field as keyof AgentFormData, value)}
              disabled={loading}
            />
          </Box>
        )}

        {/* Tab 2: Sage */}
        {activeTab === 2 && (
          <Box role="tabpanel" id="agent-tabpanel-sage" aria-labelledby="agent-tab-sage" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <SageDetailsSection
              sageName={formData.sageName}
              taxReference={formData.taxReference}
              suggestedSageName={suggestedSageName}
              isSageReady={isSageReady}
              onSageNameChange={(v) => handleFieldChange('sageName', v)}
              onTaxReferenceChange={(v) => handleFieldChange('taxReference', v)}
              disabled={loading}
              isLinkedToSage={agent?.sageDetails?.isLinkedToSage}
              isCustomer={agent?.sageDetails?.isCustomer}
              isSupplier={agent?.sageDetails?.isSupplier}
              errors={{
                sageName: errors.sageName,
                taxReference: errors.taxReference,
              }}
              entityType="Agent"
              entityId={agent?.idAgent}
              customerLinking={!!agent}
              onSageChanged={() => setSageChanged(true)}
            />
            {agent && (
              <AgentSalesRepLinkPanel
                agentId={agent.idAgent}
                agentName={agent.agentName}
                primaryEmail={agent.emailAddress}
                additionalEmails={agent.additionalEmails}
                contactNumber={agent.contactNumber}
                onChanged={() => setSageChanged(true)}
              />
            )}
            {agent && (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>Invoicing Mode</InputLabel>
                  <Select
                    value={formData.invoicingMode}
                    onChange={(e) => handleFieldChange('invoicingMode', e.target.value)}
                    label="Invoicing Mode"
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="Standard">Standard (per workflow)</MenuItem>
                    <MenuItem value="Consolidated">Consolidated</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                  <InputLabel>Default Tax Type</InputLabel>
                  <Select<number | ''>
                    value={formData.defaultSageTaxTypeId ?? ''}
                    onChange={(e) => handleFieldChange('defaultSageTaxTypeId', e.target.value === '' ? null : Number(e.target.value))}
                    label="Default Tax Type"
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">Use global default</MenuItem>
                    {taxTypes.filter(t => t.active).map(t => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name} ({t.percentage}%)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ textTransform: 'none', borderRadius: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.agentName.trim() || formData.idLab === 0 || loadingLabs}
          sx={{ textTransform: 'none', borderRadius: 1.5, minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            agent ? 'Update' : 'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentDialog;
